import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useState } from "react";

import { ContentStatusBadge } from "~/components/admin/content-status-badge";
import { FaqEditor } from "~/components/admin/faq-editor";
import { ImageUploadField } from "~/components/admin/image-upload-field";
import { ChallengeSelection } from "~/components/challenge-selection";
import { ExpertiseCategorySelection } from "~/components/expertise-category-selection";
import { FrameworkSelection } from "~/components/framework-selection";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { RichTextEditor } from "~/components/ui/rich-text-editor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import {
  approveChallengeFn,
  archiveChallengeFn,
  challengeQueryKeys,
  challengeQueryOptions,
  type ChallengeRow,
  createChallengeDraftFn,
  getChallengeExpertiseCategoryIdsFn,
  getChallengeFrameworkIdsFn,
  getChallengeRelatedIdsFn,
  getChallengesFn,
  promoteChallengeDraftFn,
  publishChallengeFn,
  restoreChallengeFn,
  returnChallengeToDraftFn,
  syncChallengeExpertiseCategoriesFn,
  syncChallengeFrameworksFn,
  syncChallengeRelatedFn,
  updateChallengeFn,
} from "~/lib/functions/cih/challenges";
import { getGroupedFrameworksFn } from "~/lib/functions/cih/frameworks";
import { getExpertiseCategories } from "~/lib/functions/expertise";
import { TRANSITION_TRIGGER_OPTIONS } from "~/lib/intake/config";

export const Route = createFileRoute("/admin/cih/challenges/$challengeId")({
  staticData: { title: "Edit Challenge" },
  loader: async ({ context, params }) => {
    const [
      data,
      expertiseCats,
      linkedCategoryIds,
      linkedFrameworkIds,
      groupedFrameworks,
      linkedRelatedIds,
      allChallenges,
    ] = await Promise.all([
      context.queryClient.ensureQueryData(
        challengeQueryOptions(params.challengeId),
      ),
      getExpertiseCategories(),
      getChallengeExpertiseCategoryIdsFn({
        data: { challengeId: params.challengeId },
      }),
      getChallengeFrameworkIdsFn({
        data: { challengeId: params.challengeId },
      }),
      getGroupedFrameworksFn(),
      getChallengeRelatedIdsFn({
        data: { challengeId: params.challengeId },
      }),
      getChallengesFn(),
    ]);
    return {
      title: data?.title ?? "Edit Challenge",
      expertiseCategories: expertiseCats,
      linkedCategoryIds,
      linkedFrameworkIds,
      groupedFrameworks,
      linkedRelatedIds,
      allChallenges: allChallenges.map((c) => ({
        id: c.id,
        title: c.title,
      })),
    };
  },
  component: ChallengeEditorPage,
});

function ChallengeEditorPage() {
  const { challengeId } = Route.useParams();
  const {
    expertiseCategories,
    linkedCategoryIds,
    linkedFrameworkIds,
    groupedFrameworks,
    linkedRelatedIds,
    allChallenges,
  } = Route.useLoaderData();
  const { data: challenge, isLoading } = useQuery(
    challengeQueryOptions(challengeId),
  );

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Challenge not found.</div>
      </div>
    );
  }

  return (
    <ChallengeEditorForm
      challengeId={challengeId}
      challenge={challenge}
      expertiseCategories={expertiseCategories}
      initialCategoryIds={linkedCategoryIds}
      initialFrameworkIds={linkedFrameworkIds}
      groupedFrameworks={groupedFrameworks}
      initialRelatedIds={linkedRelatedIds}
      allChallenges={allChallenges}
    />
  );
}

function ChallengeEditorForm({
  challengeId,
  challenge,
  expertiseCategories,
  initialCategoryIds,
  initialFrameworkIds,
  groupedFrameworks,
  initialRelatedIds,
  allChallenges,
}: {
  challengeId: string;
  challenge: ChallengeRow;
  expertiseCategories: { id: string; name: string; slug: string }[];
  initialCategoryIds: string[];
  initialFrameworkIds: string[];
  groupedFrameworks: Record<
    string,
    { id: string; name: string; type: string }[]
  >;
  initialRelatedIds: string[];
  allChallenges: { id: string; title: string }[];
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedCategoryIds, setSelectedCategoryIds] =
    useState<string[]>(initialCategoryIds);
  const [selectedFrameworkIds, setSelectedFrameworkIds] =
    useState<string[]>(initialFrameworkIds);
  const [selectedRelatedIds, setSelectedRelatedIds] =
    useState<string[]>(initialRelatedIds);
  // Exclude the current challenge from the selectable options
  const relatedChallengeOptions = allChallenges.filter(
    (c) => c.id !== challengeId,
  );

  const form = useForm({
    defaultValues: {
      title: challenge.title,
      subtitle: challenge.subtitle ?? "",
      description: challenge.description,
      heroImageUrl: challenge.heroImageUrl ?? "",
      tagline: challenge.tagline ?? "",
      icon: challenge.icon ?? "",
      body: challenge.body != null ? JSON.stringify(challenge.body) : "",
      faqs: (challenge.faqs ?? []) as { question: string; answer: string }[],
      reviewerName: challenge.reviewerName ?? "",
      reviewerCredentials: challenge.reviewerCredentials ?? "",
      reviewedDate: challenge.reviewedDate ?? "",
      intakeTrigger: challenge.intakeTrigger ?? "",
    },
  });

  const showSuccess = (msg: string): void => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: challengeQueryKeys.all });
    queryClient.invalidateQueries({
      queryKey: challengeQueryKeys.detail(challengeId),
    });
  };

  const getFormData = () => {
    const v = form.state.values;
    return {
      id: challengeId,
      title: v.title,
      subtitle: v.subtitle,
      description: v.description,
      heroImageUrl: v.heroImageUrl,
      tagline: v.tagline,
      icon: v.icon,
      body: v.body,
      faqs: v.faqs,
      reviewerName: v.reviewerName,
      reviewerCredentials: v.reviewerCredentials,
      reviewedDate: v.reviewedDate,
      intakeTrigger: v.intakeTrigger,
    };
  };

  const saveAll = async () => {
    await updateChallengeFn({ data: getFormData() });
    await Promise.all([
      syncChallengeExpertiseCategoriesFn({
        data: { challengeId, categoryIds: selectedCategoryIds },
      }),
      syncChallengeFrameworksFn({
        data: { challengeId, frameworkIds: selectedFrameworkIds },
      }),
      syncChallengeRelatedFn({
        data: {
          challengeId,
          relatedChallengeIds: selectedRelatedIds,
        },
      }),
    ]);
  };

  const updateMutation = useMutation({
    mutationFn: saveAll,
    onSuccess: () => {
      invalidate();
      showSuccess("Challenge saved.");
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (data: { data: { id: string } }) => {
      await saveAll();
      return publishChallengeFn(data);
    },
    onSuccess: () => {
      invalidate();
      showSuccess("Challenge published.");
    },
  });

  const archiveMutation = useMutation({
    mutationFn: archiveChallengeFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Challenge archived.");
    },
  });

  const createDraftMutation = useMutation({
    mutationFn: createChallengeDraftFn,
    onSuccess: (newDraft) => {
      invalidate();
      navigate({
        to: "/admin/cih/challenges/$challengeId",
        params: { challengeId: newDraft.id },
      });
    },
  });

  const promoteMutation = useMutation({
    mutationFn: async (data: { data: { draftId: string } }) => {
      await saveAll();
      return promoteChallengeDraftFn(data);
    },
    onSuccess: (promoted) => {
      queryClient.removeQueries({
        queryKey: challengeQueryKeys.detail(challengeId),
      });
      invalidate();
      navigate({
        to: "/admin/cih/challenges/$challengeId",
        params: { challengeId: promoted.id },
      });
    },
  });

  const approveMutation = useMutation({
    mutationFn: approveChallengeFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Challenge approved and published.");
    },
  });

  const returnToDraftMutation = useMutation({
    mutationFn: returnChallengeToDraftFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Challenge returned to draft.");
    },
  });

  const restoreMutation = useMutation({
    mutationFn: restoreChallengeFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Challenge restored to draft.");
    },
  });

  const handleSave = (): void => {
    updateMutation.mutate();
  };

  const anyPending =
    updateMutation.isPending ||
    publishMutation.isPending ||
    archiveMutation.isPending ||
    createDraftMutation.isPending ||
    promoteMutation.isPending ||
    approveMutation.isPending ||
    returnToDraftMutation.isPending ||
    restoreMutation.isPending;

  const mutationError =
    updateMutation.error ??
    publishMutation.error ??
    archiveMutation.error ??
    createDraftMutation.error ??
    promoteMutation.error ??
    approveMutation.error ??
    returnToDraftMutation.error ??
    restoreMutation.error;

  const isDraft = challenge.status === "draft";
  const isPublished = challenge.status === "published";
  const isInReview = challenge.status === "in_review";
  const isArchived = challenge.status === "archived";
  const hasDraftParent = challenge.draftOf != null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-medium text-gray-900">
                {challenge.title}
              </h1>
              <ContentStatusBadge status={challenge.status} />
            </div>
            <p className="mt-1 flex items-center gap-2 text-sm text-gray-500">
              <span>Challenge editor — ID: {challengeId}</span>
              {challenge.slug && challenge.status === "published" && (
                <a
                  href={`/coaching-for/${challenge.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-brand-600 hover:underline"
                >
                  View public page
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            {isDraft && !hasDraftParent && (
              <>
                <Button
                  variant="default"
                  onClick={handleSave}
                  disabled={anyPending}
                  loading={updateMutation.isPending}
                >
                  Save
                </Button>
                <Button
                  variant="primary"
                  onClick={() =>
                    publishMutation.mutate({ data: { id: challengeId } })
                  }
                  disabled={anyPending}
                  loading={publishMutation.isPending}
                >
                  Publish
                </Button>
              </>
            )}
            {isDraft && hasDraftParent && (
              <Button
                variant="default"
                onClick={handleSave}
                disabled={anyPending}
                loading={updateMutation.isPending}
              >
                Save
              </Button>
            )}
            {isPublished && (
              <>
                <Button
                  variant="default"
                  onClick={() =>
                    createDraftMutation.mutate({
                      data: { publishedId: challengeId },
                    })
                  }
                  disabled={anyPending}
                  loading={createDraftMutation.isPending}
                >
                  Create Draft
                </Button>
                <Button
                  variant="destructive"
                  onClick={() =>
                    archiveMutation.mutate({ data: { id: challengeId } })
                  }
                  disabled={anyPending}
                  loading={archiveMutation.isPending}
                >
                  Archive
                </Button>
              </>
            )}
            {isInReview && (
              <>
                <Button
                  variant="primary"
                  onClick={() =>
                    approveMutation.mutate({ data: { id: challengeId } })
                  }
                  disabled={anyPending}
                  loading={approveMutation.isPending}
                >
                  Approve
                </Button>
                <Button
                  variant="default"
                  onClick={() => {
                    const note = window.prompt("Feedback note for the author:");
                    if (note == null) return;
                    returnToDraftMutation.mutate({
                      data: { id: challengeId, note },
                    });
                  }}
                  disabled={anyPending}
                  loading={returnToDraftMutation.isPending}
                >
                  Return to Draft
                </Button>
              </>
            )}
            {isArchived && (
              <Button
                variant="default"
                onClick={() =>
                  restoreMutation.mutate({ data: { id: challengeId } })
                }
                disabled={anyPending}
                loading={restoreMutation.isPending}
              >
                Restore to Draft
              </Button>
            )}
          </div>
        </div>

        {/* Draft-of banner */}
        {hasDraftParent && (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-700">
              Editing draft of published content. Save your changes, then
              promote when ready.
            </p>
            <Button
              variant="primary"
              size="sm"
              onClick={() =>
                promoteMutation.mutate({ data: { draftId: challengeId } })
              }
              disabled={anyPending}
              loading={promoteMutation.isPending}
              className="border-amber-700! from-amber-600! to-amber-700! hover:from-amber-500! hover:to-amber-600!"
            >
              Promote to Live
            </Button>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {successMessage}
          </div>
        )}

        {mutationError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {mutationError instanceof Error
              ? mutationError.message
              : "An error occurred. Please try again."}
          </div>
        )}

        {/* Form */}
        <Card className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ch-title" className="mb-1">
                Title
              </Label>
              <form.Field name="title">
                {(field) => (
                  <Input
                    id="ch-title"
                    type="text"
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                )}
              </form.Field>
            </div>
            <div>
              <Label htmlFor="ch-slug" className="mb-1">
                Slug
              </Label>
              <Input
                id="ch-slug"
                type="text"
                value={challenge.slug ?? ""}
                readOnly
                className="bg-gray-50"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="ch-subtitle" className="mb-1">
              Subtitle{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </Label>
            <form.Field name="subtitle">
              {(field) => (
                <Input
                  id="ch-subtitle"
                  type="text"
                  value={field.state.value}
                  onValueChange={field.handleChange}
                  onBlur={field.handleBlur}
                />
              )}
            </form.Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ch-tagline" className="mb-1">
                Tagline{" "}
                <span className="font-normal text-gray-400">(optional)</span>
              </Label>
              <form.Field name="tagline">
                {(field) => (
                  <Input
                    id="ch-tagline"
                    type="text"
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                    placeholder="When Sunday nights feel heavier than they should"
                  />
                )}
              </form.Field>
            </div>
            <div>
              <Label htmlFor="ch-icon" className="mb-1">
                Icon{" "}
                <span className="font-normal text-gray-400">(optional)</span>
              </Label>
              <form.Field name="icon">
                {(field) => (
                  <Input
                    id="ch-icon"
                    type="text"
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                    placeholder="e.g. compass, heart, briefcase"
                  />
                )}
              </form.Field>
            </div>
          </div>

          <div>
            <Label className="mb-1">
              Intake Trigger{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </Label>
            <p className="mb-2 text-xs text-gray-400">
              Auto-selects the first intake question when visitors click through
              from this challenge page.
            </p>
            <form.Field name="intakeTrigger">
              {(field) => (
                <Select
                  value={field.state.value || undefined}
                  onValueChange={(v) =>
                    field.handleChange(v === "__none__" ? "" : v)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="None (no auto-select)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      None (no auto-select)
                    </SelectItem>
                    {TRANSITION_TRIGGER_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </form.Field>
          </div>

          <div>
            <Label htmlFor="ch-description" className="mb-1">
              Description
            </Label>
            <form.Field name="description">
              {(field) => (
                <Textarea
                  id="ch-description"
                  rows={3}
                  value={field.state.value}
                  onValueChange={field.handleChange}
                  onBlur={field.handleBlur}
                />
              )}
            </form.Field>
          </div>

          <form.Field name="heroImageUrl">
            {(field) => (
              <ImageUploadField
                contentType="challenges"
                contentId={challengeId}
                purpose="hero"
                currentUrl={field.state.value || null}
                onUpload={field.handleChange}
                onRemove={() => field.handleChange("")}
                label="Hero Image"
                recommendedDimensions="1200x630"
              />
            )}
          </form.Field>

          <div>
            <Label className="mb-1">Body</Label>
            <form.Field name="body">
              {(field) => (
                <RichTextEditor
                  content={field.state.value}
                  onChange={field.handleChange}
                />
              )}
            </form.Field>
          </div>
        </Card>

        {/* Expertise Categories */}
        <Card className="mt-6">
          <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
            Expertise Categories
          </h2>
          <p className="mb-4 text-sm text-gray-400">
            Coaches are found for this challenge through their existing
            expertise selections. Link the relevant categories here.
          </p>
          <ExpertiseCategorySelection
            selectedIds={selectedCategoryIds}
            onSelectionChange={setSelectedCategoryIds}
            categories={expertiseCategories}
          />
        </Card>

        {/* Frameworks */}
        <Card className="mt-6">
          <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
            Frameworks
          </h2>
          <p className="mb-4 text-sm text-gray-400">
            Link coaching frameworks and methodologies relevant to this
            challenge.
          </p>
          <FrameworkSelection
            selectedIds={selectedFrameworkIds}
            onSelectionChange={setSelectedFrameworkIds}
            groupedFrameworks={groupedFrameworks}
          />
        </Card>

        {/* Related Challenges */}
        <Card className="mt-6">
          <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
            Related Challenges
          </h2>
          <p className="mb-4 text-sm text-gray-400">
            Link challenges that are closely related to this one. Links are
            stored symmetrically — adding a challenge here also links back from
            that challenge.
          </p>
          <ChallengeSelection
            selectedIds={selectedRelatedIds}
            onSelectionChange={setSelectedRelatedIds}
            challenges={relatedChallengeOptions}
          />
        </Card>

        {/* FAQs */}
        <Card className="mt-6">
          <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
            FAQs
          </h2>
          <form.Field name="faqs">
            {(field) => (
              <FaqEditor
                value={field.state.value}
                onChange={field.handleChange}
              />
            )}
          </form.Field>
        </Card>

        {/* Trust Signal */}
        <Card className="mt-6">
          <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
            Trust Signal
          </h2>
          <p className="mb-4 text-sm text-gray-400">
            Displayed as a "Reviewed by" badge on the public page.
          </p>
          <div className="grid grid-cols-3 gap-4">
            <form.Field name="reviewerName">
              {(field) => (
                <div>
                  <Label className="mb-1">Reviewer Name</Label>
                  <Input
                    type="text"
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                    placeholder="Dr. Jane Smith"
                  />
                </div>
              )}
            </form.Field>
            <form.Field name="reviewerCredentials">
              {(field) => (
                <div>
                  <Label className="mb-1">Credentials</Label>
                  <Input
                    type="text"
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                    placeholder="PCC, LCSW"
                  />
                </div>
              )}
            </form.Field>
            <form.Field name="reviewedDate">
              {(field) => (
                <div>
                  <Label className="mb-1">Reviewed Date</Label>
                  <Input
                    type="text"
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                    placeholder="April 2026"
                  />
                </div>
              )}
            </form.Field>
          </div>
        </Card>

        {/* Sticky save for draft */}
        {isDraft && (
          <div className="mt-4 flex justify-end">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={anyPending}
              loading={updateMutation.isPending}
            >
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
